import jsPDF from "jspdf";


// Carga el logo desde public y lo convierte a dataURL
async function loadLogoDataUrl() {
  return new Promise((resolve, reject) => {
    try {
      const img = new window.Image();
      img.crossOrigin = "Anonymous";
      img.onload = function () {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = () => resolve(null);
      img.src = "/lux_logo.PNG"; // ubicado en public/lux_logo.PNG
    } catch (e) {
      resolve(null);
    }
  });
}

// Lógica de redondeo de calificaciones final
function calcularFinal(p1, p2, p3) {
  if (p1 == null || p2 == null || p3 == null) return '';
  const total = (Number(p1) + Number(p2) + Number(p3)) / 3;
  if (total === 0) return 0;
  if (total < 6) return 5;
  if (total < 7) return 6;
  if (total < 7.5) return 7;
  if (total < 8.5) return 8;
  if (total < 9.5) return 9;
  return 10;
}

// Dibuja encabezado institucional (logo + datos institución). Devuelve la Y final.
function drawInstitutionalHeader(doc, logoDataUrl, pageWidth, margin) {
  const logoSize = 18;
  const headerTopY = 12;
  if (logoDataUrl) {
    doc.addImage(logoDataUrl, "PNG", margin, headerTopY - 6, logoSize, logoSize);
  }
  const centerX = pageWidth / 2;
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(0, 0, 0);
  doc.text("Centro De Estudios Superiores CMP, A.C.", centerX, 15, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.text("Ave. Venustiano Carranza Nte. 614", centerX, 20, { align: "center" });
  doc.text("CENTRO", centerX, 25, { align: "center" });
  doc.text("MONTERREY, MONTERREY, N.L. C.P.64000", centerX, 30, { align: "center" });
  doc.text("R.F.C CES9912132F4", centerX, 35, { align: "center" });
  return 40;
}

// Dibuja la fila de encabezados de la tabla (negro). Devuelve la Y después del encabezado.
function drawTableHeader(doc, colX, tableWidth, margin, y) {
  const headerHeight = 8;
  doc.setFillColor(0, 0, 0);
  doc.rect(margin, y, tableWidth, headerHeight, "F");
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  doc.text("ASIGNATURA", colX[0] + 2, y + 6);
  doc.text("P1",     colX[1] + 2, y + 6);
  doc.text("P2",     colX[2] + 2, y + 6);
  doc.text("P3",     colX[3] + 2, y + 6);
  doc.text("FINAL",  colX[4] + 2, y + 6);
  doc.text("EXTRA.", colX[5] + 2, y + 6);
  doc.setTextColor(0, 0, 0);
  return y + headerHeight + 3;
}

function formatCalificacion(value) {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  if (Number.isNaN(n)) return '';
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function drawCicloTitle(doc, cicloNombre, margin, y) {
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(0, 0, 0);
  doc.text(`Ciclo escolar: ${cicloNombre || 'Sin ciclo'}`, margin, y);
  return y + 4;
}

function getMateriaRowHeight(doc, materia, asigWidth, lineHeight, rowPadding) {
  const lines = doc.splitTextToSize(materia?.materia_nombre || '', asigWidth - 4);
  const claveLines = materia?.materia_clave
    ? doc.splitTextToSize(materia.materia_clave, asigWidth - 4)
    : [];
  const rowHeight = lines.length * lineHeight
    + (claveLines.length > 0 ? claveLines.length * 3 + 1 : 0)
    + rowPadding * 2;

  return {
    lines,
    claveLines,
    rowHeight,
  };
}

// Genera un PDF de boleta de calificaciones para un estudiante (materias y calificaciones)
export async function generateBoletaCalificacionesPDF({ encabezado, ciclos = [], materias = [], descargar = false }) {
  const doc = new jsPDF({ orientation: "landscape" });
  const pageWidth  = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 10;
  const tableWidth = pageWidth - margin * 2;

  const ciclosData = Array.isArray(ciclos) && ciclos.length > 0
    ? ciclos
    : [{ nombre_ciclo: 'Ciclo escolar', materias: Array.isArray(materias) ? materias : [] }];
  const allMaterias = ciclosData.flatMap((c) => (Array.isArray(c?.materias) ? c.materias : []));

  // Columnas numéricas: P1, P2, P3, FINAL, EXTRA. → 20 unidades cada una
  const numColWidth = 20;
  const numColsCount = 5;

  // Calcular ancho dinámico para ASIGNATURA según el texto más largo
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  const minAsigWidth = 60;
  const maxAsigWidth = tableWidth - numColWidth * numColsCount - 4;
  let asigWidth = minAsigWidth;
  allMaterias.forEach((m) => {
    const w = doc.getTextWidth(m.materia_nombre || '');
    if (w + 4 > asigWidth) asigWidth = w + 4;
  });
  asigWidth = Math.min(asigWidth, maxAsigWidth);

  // Posiciones X de cada columna
  const colX = [
    margin,
    margin + asigWidth,
    margin + asigWidth + numColWidth,
    margin + asigWidth + numColWidth * 2,
    margin + asigWidth + numColWidth * 3,
    margin + asigWidth + numColWidth * 4,
  ];

  // Cargar logo
  const logoDataUrl = await loadLogoDataUrl();

  // --- Página 1: encabezado institucional ---
  let y = drawInstitutionalHeader(doc, logoDataUrl, pageWidth, margin);

  // Datos del estudiante
  y += 2;
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("Nombre:", margin, y);
  doc.setFont("helvetica", "normal");
  doc.text(`${encabezado?.nombre_completo || ''}`, margin + 25, y);
  y += 6;
  doc.setFont("helvetica", "bold");
  doc.text("Matrícula:", margin, y);
  doc.setFont("helvetica", "normal");
  doc.text(`${encabezado?.matricula || ''}`, margin + 25, y);
  y += 6;
  doc.setFont("helvetica", "bold");
  doc.text("Programa:", margin, y);
  doc.setFont("helvetica", "normal");
  doc.text(`${encabezado?.programa || ''}`, margin + 25, y);
  y += 6;
  doc.setFont("helvetica", "bold");
  doc.text("Campus:", margin, y);
  doc.setFont("helvetica", "normal");
  doc.text(`${encabezado?.campus || ''}`, margin + 25, y);
  y += 8;

  // Filas de materias
  const lineHeight = 5;
  const rowPadding = 1;
  const bottomMargin = pageHeight - margin;

  ciclosData.forEach((ciclo, cicloIndex) => {
    const cicloNombre = ciclo?.nombre_ciclo || 'Sin ciclo';
    const materiasCiclo = Array.isArray(ciclo?.materias) ? ciclo.materias : [];
    const blockHeaderHeight = 2 + 4 + 2 + 8 + 3;

    let minFirstRowHeight = 8;
    if (materiasCiclo.length > 0) {
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      const firstRow = getMateriaRowHeight(doc, materiasCiclo[0], asigWidth, lineHeight, rowPadding);
      minFirstRowHeight = firstRow.rowHeight;
    }

    // Antes de cada ciclo (excepto el primero), validar espacio y repetir encabezado de tabla.
    if (cicloIndex > 0) {
      const espacioNecesario = 18;
      if (y + espacioNecesario > bottomMargin) {
        doc.addPage();
        y = drawInstitutionalHeader(doc, logoDataUrl, pageWidth, margin);
        y += 5;
      } else {
        y += 4;
      }
    }

    // Evitar encabezado huérfano: si no cabe ciclo + tabla + primera fila, saltar antes de dibujar.
    if (y + blockHeaderHeight + minFirstRowHeight > bottomMargin) {
      doc.addPage();
      y = drawInstitutionalHeader(doc, logoDataUrl, pageWidth, margin);
      y += 5;
    }

    y = drawCicloTitle(doc, cicloNombre, margin, y + 2);
    y += 2;
    y = drawTableHeader(doc, colX, tableWidth, margin, y);

    materiasCiclo.forEach((m) => {
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");

    // Dividir el nombre de la materia en líneas que quepan en el ancho de ASIGNATURA
    const { lines, claveLines, rowHeight } = getMateriaRowHeight(doc, m, asigWidth, lineHeight, rowPadding);

    // Salto de página si no cabe la fila
    if (y + rowHeight > bottomMargin) {
      doc.addPage();
      y = drawInstitutionalHeader(doc, logoDataUrl, pageWidth, margin);
      y += 5;
      y = drawCicloTitle(doc, cicloNombre, margin, y + 2);
      y += 2;
      y = drawTableHeader(doc, colX, tableWidth, margin, y);
    }

    // Nombre de asignatura (puede ser multilínea)
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.text(lines, colX[0] + 2, y + rowPadding + lineHeight - 1);

    // Clave en gris bajo el nombre
    if (claveLines.length > 0) {
      doc.setFontSize(7);
      doc.setTextColor(100, 100, 100);
      doc.text(claveLines, colX[0] + 2, y + rowPadding + lines.length * lineHeight + 2);
    }

    // Columnas numéricas alineadas verticalmente al centro del nombre
    const calY = y + rowPadding + lineHeight - 1;
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.text(formatCalificacion(m.parcial_1), colX[1] + 2, calY);
    doc.text(formatCalificacion(m.parcial_2), colX[2] + 2, calY);
    doc.text(formatCalificacion(m.parcial_3), colX[3] + 2, calY);
    const finalCal = m.final != null ? m.final : calcularFinal(m.parcial_1, m.parcial_2, m.parcial_3);
    doc.text(formatCalificacion(finalCal), colX[4] + 2, calY);
    doc.text(formatCalificacion(m.extraordinario), colX[5] + 2, calY);

    // Línea divisoria entre filas
      y += rowHeight;
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, y, margin + tableWidth, y);
    });

    if (materiasCiclo.length === 0) {
      doc.setFontSize(9);
      doc.setFont("helvetica", "italic");
      doc.setTextColor(100, 100, 100);
      doc.text("Sin materias registradas en este ciclo.", margin + 2, y + 5);
      doc.setTextColor(0, 0, 0);
      y += 8;
    }
  });

  // Descargar o devolver dataURL
  if (descargar) {
    doc.save("boleta_calificaciones.pdf");
  }
  return doc.output("datauristring");
}
